import { useState, useEffect } from 'react';
import {
  Heading,
  BodyText,
  MetricCard,
  Card,
  Badge,
} from '@aaimplatform/ui';
import type { components as CrtComponents } from '@aaimplatform/institutional-types/generated/cooperative-reserve-token';
import { cooperativeReserveTokenClient } from '../../api/typed-clients';
// Side-effect import to ensure auth middleware is registered on the client.
import '../../api/client';

type NetworkMetrics = CrtComponents['schemas']['NetworkMetrics'];
type RecentActivity = CrtComponents['schemas']['RecentActivity'];

export function SyndicationDashboard() {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<NetworkMetrics | null>(null);
  const [activity] = useState<RecentActivity[]>([]);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Reserve Parity card moved to /syndication/crt (Lane B1, audit
        // section 3.2). Network Dashboard fetches metrics only; the parity
        // breakdown lives in the CRT-scoped surface.
        const metricsRes = await cooperativeReserveTokenClient.GET(
          '/api/v1/crt/network/metrics',
        );
        if (metricsRes.data) setMetrics(metricsRes.data);
        if (metricsRes.error) {
          setServiceUnavailable(true);
        }
      } catch {
        // Backend service for cooperative-reserve-token is not yet deployed
        // (port 9571). Surface honest "service unavailable" empty state
        // instead of a misleading data view. See typed-clients.ts for the
        // deployment plan.
        setServiceUnavailable(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(amount);

  const formatPercent = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 1 }).format(value);

  const activityBadgeVariant = (type: RecentActivity['type']) => {
    switch (type) {
      case 'offering': return 'info' as const;
      case 'commitment': return 'success' as const;
      case 'distribution': return 'neutral' as const;
      case 'mint': return 'success' as const;
      case 'burn': return 'warning' as const;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center mt-8">
        <BodyText variant="ui">Loading network data...</BodyText>
      </div>
    );
  }

  if (serviceUnavailable) {
    return (
      <div>
        <BodyText variant="ui" className="mb-6 text-aaim-stone-600">
          CRT token lifecycle, loan syndication, and inter-FI liquidity management
        </BodyText>
        <Card className="p-8 text-center">
          <Heading level={2} size="card" className="font-sans mb-2">
            Service Not Yet Available
          </Heading>
          <BodyText variant="ui" className="text-aaim-stone-600 max-w-lg mx-auto">
            The Cooperative Reserve Token backend service (port 9571) has not
            been deployed. The OpenAPI specification and typed clients are in
            place; the service itself is on the deployment roadmap.
          </BodyText>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <BodyText variant="ui" className="mb-6 text-aaim-stone-600">
        CRT token lifecycle, loan syndication, and inter-FI liquidity management
      </BodyText>

      {/* Reserve Parity card moved to /syndication/crt -- see Lane B1 of
          sprint-portal-uxia-audit (audit section 3.2 / Lane 1 IA observation
          4.2). The CRT-scoped surface owns the parity breakdown. */}

      {/* Network Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total CRT Supply"
          value={formatCurrency(metrics?.totalSupply ?? 0)}
          description="1:1 USD parity"
        />
        <MetricCard
          label="Active Participants"
          value={String(metrics?.activeParticipants ?? 0)}
          description={`${metrics?.totalParticipants ?? 0} registered`}
        />
        <MetricCard
          label="Active Syndications"
          value={String(metrics?.activeSyndications ?? 0)}
        />
        <MetricCard
          label="Pool Utilization"
          value={formatPercent(metrics?.poolUtilization ?? 0)}
          description="CRT deployed in participations"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Interest Distributed"
          value={formatCurrency(metrics?.totalInterestDistributed ?? 0)}
          description="Cumulative"
        />
        <MetricCard
          label="Network Avg Yield"
          value={formatPercent(metrics?.weightedAverageYield ?? 0)}
          description="Annualized"
        />
        <MetricCard
          label="30-Day Volume"
          value={formatCurrency(metrics?.thirtyDayVolume ?? 0)}
        />
        <MetricCard
          label="Fiat Reserve"
          value={formatCurrency(metrics?.totalFiatCustody ?? 0)}
          description="Trust Company custody"
        />
      </div>

      {/* Recent Activity */}
      <Card className="p-4">
        <Heading level={2} size="label" className="font-sans mb-4">Recent Network Activity</Heading>
        <div className="space-y-3">
          {activity.map((item) => (
            <div key={item.id} className="flex items-center justify-between py-2 border-b border-aaim-stone-200 last:border-0">
              <div className="flex items-center gap-3">
                <Badge variant={activityBadgeVariant(item.type)}>
                  {item.type}
                </Badge>
                <div>
                  <BodyText variant="ui" className="font-medium">{item.description}</BodyText>
                  <BodyText variant="ui" className="text-aaim-stone-500 text-sm">{item.institution}</BodyText>
                </div>
              </div>
              <div className="text-right">
                <BodyText variant="ui" className="font-medium">{formatCurrency(item.amount)}</BodyText>
                <BodyText variant="ui" className="text-aaim-stone-500 text-sm">{item.timestamp}</BodyText>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
